/* ----------------------------------------------------------------------------
   M0 · Set Up Courier Account Code — onboarding wizard  (route /supply/partners/onboard)
   React port of m0.js renderOnboard() (mockup v6). useReducer-driven step machine
   with the mockup's dynamic step plan:
     entry → [details · contacts (new-parent only)] → dms (Profile & Service)
           → code → serv → tat → [qc (reverse only)] → review & commit → done
   COD remittance lives in COD Reconciliation → Cycle Config; Credentials,
   Commercials & Courier Zones are separate ribbon modules — none are steps here.
   Compact labelled progress strip on top (no side rail), and localStorage draft
   autosave + resume. */
import React, {useEffect, useMemo, useReducer, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import {Box, Stack, Typography, Button} from '@mui/material'
import {alpha} from '@mui/material/styles'
import PageTitle from '../../../components/PageTitle'
import {WizardStepper, SupplyHero, HERO_BTN} from '../components'
import WIZARD_CSS from './wizardCss'
import {saveDraft, clearDraft, loadDraft, defaultAttrs} from './data'
import BulkAttributeUploadDialog from '../../couriers/BulkAttributeUploadDialog'

import StepEntry from './steps/StepEntry'
import StepDetails from './steps/StepDetails'
import StepContacts from './steps/StepContacts'
import StepDms from './steps/StepDms'
import StepCode from './steps/StepCode'
import StepMode from './steps/StepMode'
import StepServ from './steps/StepServ'
import StepQc from './steps/StepQc'
import StepReview from './steps/StepReview'
import StepDone from './steps/StepDone'

/* ───────── initial W state (mirrors the mockup `W`) ───────── */
function initState() {
  return {
    step: 0,
    parentMode: null,
    S: {direction: '', mode: '', shipmentValue: '', paymentMode: '', freightDir: []},
    A: {name: '', legal: '', verticals: [], gst: '', integration: '', website: '', address: ''},
    pocs: [{name: '', desig: '', roles: [], email: '', m1: '', m2: '', linkedin: '', notes: ''}],
    C: {slaD2C: '', slaPTL: '', freq: '', mode: '', notes: '', codDPlus: '', codTimesPerWeek: ''},
    D: {code: '', display: '', prozo: '', parentId: '', serviceTypes: [], serviceTypePrimary: '', serviceTypeSecondary: '', email: '', status: 'Draft', attrs: defaultAttrs()},
    /* integration mode chosen on the Click Post / Direct Integration step. mode is
       either 'CLICKPOST' (cpId lives here) or 'DIRECT' (PCIS courier-account fields).
       `fields` holds the DIRECT-mode values, keyed by the resolved courier schema's
       field keys (see diCourierSchemas.js) — different couriers have entirely
       different fields, so this is a free-form bag rather than named properties. */
    DI: {mode: '', cpId: '', partnerCode: '', fields: {}, extras: []},
    COMM: {method: 'manual', rateCard: '', baseRate: '', fuelPct: '', codFeePct: '', codFeeMin: '', gstPct: '18', minWt: '', matrix: null, edits: 0},
    AI: {status: 'idle', file: '', confidence: 0, confirmed: false, rates: null, tc: null, edits: 0},
    SVC: {pickup: '', dropPrepaid: '', dropCod: '', dropWarehousePrepaid: '', pickupEndPrepaid: '', pickupEndExchange: ''},
    /* actual File objects per serviceability lane — NOT persisted to localStorage
       (Files don't survive JSON); on draft resume the user re-attaches files. */
    SVCFILES: {},
    SVCUI: {tab: 'drop', pill: 'prepaid', autoFetch: false, moreOpen: false},
    TAT: {},
    QC: {mode: '', stdParams: [], reasonFile: '', reasonMatrix: null, moreOpen: false},
    review: {generated: false, reviewed: false},
    /* backend refs: parentRef = {id, name, integrationMethod} of the created/selected
       parent; commitDone tracks finished commit tasks so a failed commit can resume. */
    parentRef: null,
    commitDone: {},
    committedParentId: null,
  }
}

/* generic patch reducer: PATCH merges a partial slice; SET replaces a slice; STEP sets step. */
function reducer(state, action) {
  switch (action.type) {
    case 'STEP':
      return {...state, step: action.step}
    case 'NEXT':
      return {...state, step: state.step + 1}
    case 'BACK':
      return {...state, step: Math.max(0, state.step - 1)}
    case 'PATCH':
      return {...state, [action.slice]: {...state[action.slice], ...action.value}}
    case 'SET':
      return {...state, [action.slice]: action.value}
    case 'FIELD':
      return {...state, [action.slice]: {...state[action.slice], [action.key]: action.value}}
    case 'RESTORE': {
      const base = initState()
      const v = action.value || {}
      /* deep-merge D/DI so drafts saved before `attrs`/`DI` existed keep the defaults */
      return {
        ...base,
        ...v,
        D: {...base.D, ...(v.D || {}), attrs: {...base.D.attrs, ...((v.D || {}).attrs || {})}},
        DI: {...base.DI, ...(v.DI || {})},
      }
    }
    case 'RESET':
      return initState()
    default:
      return state
  }
}

/* ───────── dynamic step plan (mockup v6) ─────────
   New-parent path additionally captures parent identity (Courier Details + Contacts);
   the per-account-code flow is identical for new & existing parents. */
function buildPlan(W) {
  const s = [{key: 'entry', label: 'Parent'}]
  if (!W.parentMode) return s
  const rev = W.S.direction === 'Reverse'
  if (W.parentMode === 'new') {
    s.push({key: 'details', label: 'Courier Details'})
    s.push({key: 'contacts', label: 'Contacts & Hierarchy'})
    /* COD remittance moved to COD Reconciliation → Cycle Config — removed here to avoid duplication */
  }
  s.push({key: 'dms', label: 'Direction, Mode & Service Type'})
  s.push({key: 'code', label: 'Account Code'})
  /* Click Post / Direct Integration step — only for the NEW-parent flow. For adding
     into an EXISTING parent, DI accounts are now managed from PCIS Health → Manage
     (Add), so this step and its courier-accounts call are dropped there. */
  if (W.parentMode === 'new') s.push({key: 'mode', label: 'Click Post / Direct Integration'})
  /* Credentials, Commercials & Courier Zones are separate ribbon modules — not part of this flow */
  s.push({key: 'serv', label: 'Serviceability'})
  /* Courier TAT is configured in the dedicated Rates & TAT Update tab, not here */
  if (rev) s.push({key: 'qc', label: 'QC Setup'})
  s.push({key: 'review', label: 'Review & Commit'})
  s.push({key: 'done', label: 'Complete'})
  return s
}

export default function OnboardWizard() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const resumeParentId = params.get('resume') || '' // deep-link: resume a DRAFT parent's onboarding
  const [W, dispatch] = useReducer(reducer, undefined, initState)
  const [pendingDraft, setPendingDraft] = useState(null)
  const [bulkOpen, setBulkOpen] = useState(false)

  /* on open, offer to restore a locally-autosaved draft (unless a ?resume deep-link wins) */
  useEffect(() => {
    if (resumeParentId) return
    const d = loadDraft()
    if (d) setPendingDraft(d)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const plan = useMemo(() => buildPlan(W), [W])
  const idx = (key) => plan.findIndex((s) => s.key === key)
  const curKey = (plan[W.step] || {}).key
  const lastFillable = W.step === plan.length - 2 // step before "done"

  const goNext = () => dispatch({type: 'NEXT'})
  const goBack = () => dispatch({type: 'BACK'})

  /* autosave: persist the draft whenever a non-entry/done step renders; clear once complete */
  useEffect(() => {
    if (!curKey || curKey === 'entry') return
    if (curKey === 'done') {
      clearDraft()
      return
    }
    saveDraft(W)
  }, [W, curKey])

  /* compact, scalable progress header — counter enumerates the same set as the node strip */
  const real = plan.filter((s) => s.key !== 'done' && s.key !== 'entry')
  const total = real.length
  const cur = W.step - 1
  const showProg = total > 0 && cur >= 0 && curKey !== 'done'
  const ctx =
    (W.parentMode === 'new' ? 'New parent' : 'Existing parent') +
    (W.S.direction ? ' · ' + W.S.direction + (W.S.mode ? ' / ' + W.S.mode : '') : '')

  const stepProps = {W, dispatch, idx, goNext, goBack, navigate, last: lastFillable}

  let stepEl = null
  switch (curKey) {
    case 'entry':
      stepEl = <StepEntry {...stepProps} resumeParentId={resumeParentId} />
      break
    case 'details':
      stepEl = <StepDetails {...stepProps} />
      break
    case 'contacts':
      stepEl = <StepContacts {...stepProps} />
      break
    case 'dms':
      stepEl = <StepDms {...stepProps} />
      break
    case 'code':
      stepEl = <StepCode {...stepProps} />
      break
    case 'mode':
      stepEl = <StepMode {...stepProps} />
      break
    case 'serv':
      stepEl = <StepServ {...stepProps} />
      break
    case 'qc':
      stepEl = <StepQc {...stepProps} />
      break
    case 'review':
      stepEl = <StepReview {...stepProps} />
      break
    case 'done':
    default:
      stepEl = <StepDone {...stepProps} />
      break
  }

  return (
    <>
      <PageTitle>Set Up Courier Account Code</PageTitle>
      <style>{WIZARD_CSS}</style>
      <SupplyHero
        icon={<AddBusinessOutlinedIcon />}
        title='Set Up Courier Account Code'
        subtitle='Create a courier account code under a new or existing parent. Progress auto-saves as you go.'
        actions={
          <>
            <Button variant='outlined' startIcon={<UploadFileOutlinedIcon style={{fontSize: 16}} />} onClick={() => setBulkOpen(true)} sx={HERO_BTN}>
              Bulk Attribute Upload
            </Button>
            <Button variant='outlined' startIcon={<CloseIcon style={{fontSize: 16}} />} onClick={() => navigate('/supply/partners')} sx={HERO_BTN}>
              Cancel
            </Button>
          </>
        }
      />
      <BulkAttributeUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />

      {pendingDraft && curKey === 'entry' ? (
        <Box sx={{borderRadius: 3, border: 1, borderColor: (th) => alpha(th.palette.warning.main, 0.4), borderLeft: '4px solid #D97706', bgcolor: (th) => alpha(th.palette.warning.main, 0.07), p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap'}}>
          <Box sx={{flex: 1, minWidth: 240}}>
            <Typography sx={{fontWeight: 800, fontSize: 14, color: 'text.primary'}}>Resume your unfinished onboarding?</Typography>
            <Typography sx={{fontSize: 12.5, color: 'text.secondary', mt: 0.25}}>
              You have a saved draft
              {(() => {
                const dW = (pendingDraft && pendingDraft.W) || {}
                const n = (dW.parentRef && dW.parentRef.name) || (dW.A && dW.A.name) || (dW.D && dW.D.code) || ''
                return n ? ' for ' + n : ''
              })()}
              . Pick up where you left off, or discard it and start fresh. (Uploaded files aren’t saved — you’ll re-attach them.)
            </Typography>
          </Box>
          <Stack direction='row' spacing={1}>
            <Button variant='outlined' onClick={() => { clearDraft(); setPendingDraft(null) }} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
              Discard
            </Button>
            <Button variant='contained' disableElevation onClick={() => { dispatch({type: 'RESTORE', value: pendingDraft.W}); setPendingDraft(null) }} sx={{borderRadius: '10px', textTransform: 'none', fontWeight: 600}}>
              Resume draft
            </Button>
          </Stack>
        </Box>
      ) : null}

      {showProg ? (
        <div style={{maxWidth: 1400}}>
          <WizardStepper steps={real} current={cur} contextLabel={ctx || null} />
        </div>
      ) : null}

      {/* setup steps live in the top progress strip; content uses the full width (no side rail).
         While an unresolved resume-draft banner is showing, the step below is greyed out and
         non-interactive until the user chooses Resume or Discard. */}
      {(() => {
        const draftGate = !!pendingDraft && curKey === 'entry'
        return (
          <div
            aria-hidden={draftGate || undefined}
            style={{
              minWidth: 0,
              ...(draftGate
                ? {opacity: 0.45, pointerEvents: 'none', filter: 'grayscale(0.35)', userSelect: 'none'}
                : null),
            }}
          >
            {stepEl}
          </div>
        )
      })()}
    </>
  )
}
